import React, { useEffect, useRef, useState } from "react";
import { Check, AlertTriangle, Lock, ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { studentShortLabel } from "@/lib/utils-app";

export const toMin = (hhmm) => {
  const [h, m] = String(hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const fromMin = (t) =>
  `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;

const covers = (ranges, s, e) =>
  (ranges || []).some((r) => toMin(r.start) <= s && toMin(r.end) >= e);

const overlaps = (ranges, s, e) =>
  (ranges || []).some((r) => s < toMin(r.end) && toMin(r.start) < e);

export function cellState(row, idx, slots, slotMinutes, teacherBusy) {
  const s = toMin(slots[idx]);
  const e = s + slotMinutes;
  return {
    busy: overlaps(row.busy, s, e),
    available: covers(row.availability, s, e),
    teacherBusy: overlaps(teacherBusy, s, e),
    teacherBlock: (teacherBusy || []).some(
      (r) => r.kind === "block" && s < toMin(r.end) && toMin(r.start) < e),
  };
}

export function selectionRange(sel) {
  if (!sel) return null;
  return { from: Math.min(sel.from, sel.to), to: Math.max(sel.from, sel.to) };
}

function Cell({ state, selected, label, onClick, testid, locked }) {
  let cls = "bg-rose-100 hover:bg-rose-200 text-rose-400";
  if (state.available) cls = "bg-emerald-200 hover:bg-emerald-300 text-emerald-800";
  // V1.84 · Solapes de la agenda del profesor en amarillo dorado; los bloqueos
  // del día a día, en dorado oscuro.
  if (state.teacherBusy) cls = "bg-amber-400 hover:bg-amber-500 text-amber-950";
  if (state.teacherBlock) cls = "bg-amber-600 text-white hover:bg-amber-700";
  if (locked) cls = "bg-neutral-400 text-neutral-700 cursor-not-allowed";
  if (selected) {
    cls = state.available
      ? "bg-amber-300 text-amber-950 ring-2 ring-amber-500"
      : "bg-amber-200 text-amber-900 ring-2 ring-amber-500 ring-dashed";
  }
  return (
    <td className="p-0">
      <button
        type="button"
        data-testid={testid}
        onClick={locked ? undefined : onClick}
        title={
          locked
            ? "Ya tiene clase a esta hora"
            : state.teacherBlock
            ? "Bloqueo de indisponibilidad del profesor"
            : state.teacherBusy
            ? "Solapa con otra clase del profesor"
            : state.available
            ? "Disponible"
            : "Fuera de su disponibilidad declarada"
        }
        className={
          "w-full h-9 text-[10px] font-mono font-semibold border-r border-white/70 transition-colors relative " + cls
        }
      >
        {locked ? <Lock className="h-3 w-3 mx-auto" /> : label}
        {selected && state.teacherBusy && (
          <AlertTriangle className="h-3 w-3 absolute top-0 right-0 text-red-700" />
        )}
      </button>
    </td>
  );
}

function StatsCell({ stats, tooltips }) {
  if (!stats) return <span className="text-[10px] text-neutral-300">···</span>;
  return (
    <div className="grid grid-cols-3 gap-x-1.5 text-[10px] font-mono leading-tight whitespace-nowrap">
      <div title={tooltips.C}><span className="text-neutral-500">C:</span> <b>{stats.contracted}</b></div>
      <div title={tooltips.R}><span className="text-neutral-500">R:</span> <b>{stats.done}</b></div>
      <div title={tooltips.P}><span className="text-neutral-500">P:</span> <b>{stats.scheduled}</b></div>
      <div title={tooltips.NA}><span className="text-neutral-500">NA:</span> <b>{stats.missed}</b></div>
      <div title={tooltips.A}><span className="text-neutral-500">A:</span> <b>{stats.adjustments > 0 ? "+" : ""}{stats.adjustments}</b></div>
      <div title={tooltips.Rest} className={"font-bold " + (stats.remaining < 0 ? "text-red-700" : stats.remaining < 3 ? "text-amber-700" : "text-emerald-700")}>
        <span className="text-neutral-500">Rest:</span> {stats.remaining}
      </div>
    </div>
  );
}

// Nombres largos recortados a 14 caracteres (el completo queda en el tooltip).
const cut14 = (s) => {
  const t = String(s || "").trim();
  if (!t) return "—";
  return t.length > 14 ? `${t.slice(0, 14)}…` : t;
};

export const STAT_TOOLTIPS = {  C: "C · Clases contratadas (compradas)",
  R: "R · Clases realizadas (asistió)",
  P: "P · Clases programadas, aún por dar (futuras)",
  NA: "NA · Faltas (no asistió)",
  A: "A · Ajustes manuales (+/−)",
  Rest: "Rest · Clases restantes = C − (R + P + NA) + A",
};

export default function PlannerBoardGrid({
  data, selection, grouped, onToggleCell, onToggleGroup, onOk, slotMinutes,
  mode = "citas", availDraft = {}, onToggleAvail,
  sortBy = "", onSort, retryMode = {}, onRetryMode, scrollRef,
  expanded, onToggleExpand, renderExpanded,
}) {
  const slots = data.slots || [];
  const teacherBusy = data.teacher_busy || [];
  const innerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => setContainerW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString().slice(0, 10);
  const Th = ({ field, children, className = "", title }) => (
    <th className={"px-2 py-2 font-semibold " + className}>
      <button
        type="button"
        data-testid={`board-sort-${field}`}
        onClick={() => onSort(field)}
        className={"inline-flex items-center gap-1 hover:text-neutral-900 " + (sortBy === field ? "text-orange-700" : "")}
        title={title || "Ordenar por esta columna"}
      >
        {children}
        <ArrowUpDown className="h-3 w-3 opacity-60" />
      </button>
    </th>
  );
  return (
    <div
      className="card-surface overflow-x-auto"
      ref={(el) => { innerRef.current = el; if (scrollRef) scrollRef.current = el; }}
      data-testid="planner-board-grid"
    >
      <table className="min-w-max text-xs border-collapse">
        <thead className="bg-neutral-50 text-neutral-600 text-left">
          <tr>
            <th className="px-2 py-2 sticky left-0 bg-neutral-50 z-20 font-semibold">Grupo</th>
            <Th field="nombre" className="min-w-[180px] sticky left-[52px] bg-neutral-50 z-20">Alumno</Th>
            <Th field="phr"><span title="Punto habitual de recogida">PHR</span></Th>
            <Th field="cp">CP</Th>
            <Th field="barrio">Barrio</Th>
            <Th field="ultimo_examen" title="Fecha del último examen práctico con resultado">
              Últ. examen
            </Th>
            <th className="px-2 py-2 font-semibold" title="Fecha mínima para volver a examinarse: 2 = 10 días hábiles · 2+ = 15 días hábiles">
              Reexamen
            </th>
            <Th field="proximo_examen" title="Próximo examen práctico convocado">
              Próx. examen
            </Th>
            {slots.map((s) => (
              <th key={s} className="px-0 py-2 font-mono font-semibold text-[9px] text-center w-9">
                {s}
              </th>
            ))}
            <th className="px-3 py-2 font-semibold">Clases</th>
            <th className="px-2 py-2 font-semibold text-center">Crear</th>
          </tr>
        </thead>
        <tbody>
          {data.students.map((row) => {
            const sel = selectionRange(selection[row.id]);
            const count = sel ? sel.to - sel.from + 1 : 0;
            const start = sel ? slots[sel.from] : "";
            const end = sel ? fromMin(toMin(slots[sel.to]) + slotMinutes) : "";
            const overBono = sel && count > (row.stats?.remaining ?? 0);
            const clash = sel && Array.from({ length: count }, (_, k) =>
              cellState(row, sel.from + k, slots, slotMinutes, teacherBusy).teacherBusy).some(Boolean);
            return (
              <React.Fragment key={row.id}>
              <tr data-testid={`board-row-${row.id}`} className="border-t border-neutral-100 hover:bg-neutral-50/60">
                <td className="px-2 py-1 sticky left-0 bg-white z-20">
                  <input
                    type="checkbox"
                    data-testid={`board-group-${row.id}`}
                    checked={grouped.has(row.id)}
                    onChange={() => onToggleGroup(row.id)}
                    className="h-4 w-4 accent-orange-600"
                  />
                </td>
                <td className="px-2 py-1 font-semibold whitespace-nowrap sticky left-[52px] bg-white z-20">
                  <button
                    type="button"
                    data-testid={`board-expand-${row.id}`}
                    onClick={() => onToggleExpand?.(row.id)}
                    title="Planificar varias semanas (pack de clases)"
                    className="align-middle mr-1 text-neutral-500 hover:text-orange-700"
                  >
                    {expanded?.has(row.id)
                      ? <ChevronDown className="h-4 w-4" />
                      : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {studentShortLabel(row) || row.name}
                  {row.incident && (
                    <span title={row.incident} className="ml-1 text-[9px] font-bold text-red-700">● baja</span>
                  )}
                  {row.availability.length === 0 && (
                    <span className="ml-1 text-[9px] text-neutral-400">sin disponibilidad</span>
                  )}
                </td>
                <td className="px-2 py-1 text-[11px] text-neutral-700 whitespace-nowrap" title={row.pickup_name}>
                  {cut14(row.pickup_name)}
                </td>
                <td className="px-2 py-1 font-mono text-[10px] text-neutral-600">{row.codigo_postal || "—"}</td>
                <td className="px-2 py-1 text-[11px] text-neutral-600 whitespace-nowrap" title={row.neighborhood}>
                  {cut14(row.neighborhood)}
                </td>
                <td className="px-2 py-1 text-[10px] font-mono whitespace-nowrap">
                  {row.last_exam_date ? (
                    <span title={`Resultado: ${row.last_exam_result || "—"}`}>
                      {row.last_exam_date.split("-").reverse().join("/")}
                      <span className={
                        "block text-[9px] font-sans font-semibold " +
                        (row.last_exam_result === "apto" ? "text-emerald-700" : "text-red-700")
                      }>
                        {row.last_exam_result || ""}
                      </span>
                    </span>
                  ) : <span className="text-neutral-300">—</span>}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {row.last_exam_date ? (
                    <div className="flex items-center gap-1">
                      <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden">
                        {["2", "2plus"].map((m) => (
                          <button
                            key={m}
                            type="button"
                            data-testid={`board-retry-${m}-${row.id}`}
                            onClick={() => onRetryMode(row.id, m)}
                            title={m === "2" ? "2ª convocatoria · 10 días hábiles" : "3ª o posterior · 15 días hábiles"}
                            className={
                              "px-1.5 h-6 text-[10px] font-bold " +
                              ((retryMode[row.id] || (row.suspensos_count >= 2 ? "2plus" : "2")) === m
                                ? "bg-neutral-900 text-white"
                                : "text-neutral-500 hover:bg-neutral-100")
                            }
                          >
                            {m === "2" ? "2" : "2+"}
                          </button>
                        ))}
                      </div>
                      <span
                        data-testid={`board-retry-date-${row.id}`}
                        className={
                          "text-[10px] font-mono font-bold " +
                          ((row.retry_date?.[retryMode[row.id] || (row.suspensos_count >= 2 ? "2plus" : "2")] || "") > today
                            ? "text-red-700"
                            : "text-emerald-700")
                        }
                      >
                        {(row.retry_date?.[retryMode[row.id] || (row.suspensos_count >= 2 ? "2plus" : "2")] || "—")
                          .split("-").reverse().join("/")}
                      </span>
                    </div>
                  ) : <span className="text-neutral-300 text-[10px]">—</span>}
                </td>
                <td className="px-2 py-1 text-[10px] font-mono whitespace-nowrap"
                  data-testid={`board-next-exam-${row.id}`}>
                  {row.next_exam_date ? (
                    <span className="font-bold text-orange-700" title="Próximo examen práctico convocado">
                      {row.next_exam_date.split("-").reverse().join("/")}
                      <span className="block text-[9px] font-sans text-neutral-500">{row.next_exam_time}</span>
                    </span>
                  ) : <span className="text-neutral-300">—</span>}
                </td>
                {slots.map((s, idx) => {
                  const base = cellState(row, idx, slots, slotMinutes, teacherBusy);
                  const editing = mode === "disponibilidad";
                  const draft = availDraft[row.id];
                  const state = editing
                    ? { ...base, available: draft ? draft.has(idx) : base.available }
                    : base;
                  return (
                    <Cell
                      key={s}
                      testid={`board-cell-${row.id}-${idx}`}
                      state={state}
                      locked={!editing && base.busy}
                      selected={!editing && !!sel && idx >= sel.from && idx <= sel.to}
                      label={toMin(s) % 60 === 0 ? String(Math.floor(toMin(s) / 60)) : "·"}
                      onClick={() => (editing ? onToggleAvail(row.id, idx) : onToggleCell(row.id, idx))}
                    />
                  );
                })}
                <td className="px-3 py-1">
                  <StatsCell stats={row.stats} tooltips={STAT_TOOLTIPS} />
                </td>
                <td className="px-2 py-1 text-center whitespace-nowrap">
                  <button
                    type="button"
                    data-testid={`board-ok-${row.id}`}
                    disabled={!sel}
                    onClick={() => onOk(row, { time: start, end_time: end, classes: count })}
                    className={
                      "inline-flex items-center gap-1 h-7 px-2 rounded-lg text-[11px] font-bold transition-colors " +
                      (sel
                        ? clash
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-neutral-800 text-white hover:bg-neutral-900"
                        : "bg-neutral-100 text-neutral-400 cursor-not-allowed")
                    }
                    title={sel ? `Crear cita ${start}–${end} (${count} clases)` : "Pinta las horas en la rejilla"}
                  >
                    <Check className="h-3 w-3" /> OK
                  </button>
                  {sel && (
                    <div className="text-[9px] font-mono mt-0.5 text-neutral-600">
                      {start}–{end} · {count}cl
                      {overBono && <span className="block text-red-700 font-bold">sin bono</span>}
                      {clash && <span className="block text-red-700 font-bold">solapa prof.</span>}
                    </div>
                  )}
                </td>
              </tr>
              {expanded?.has(row.id) && (
                <tr data-testid={`board-row-expanded-${row.id}`}>
                  <td colSpan={slots.length + 10} className="p-0">
                    {/* El panel queda fijo a la izquierda y con el ancho visible
                        de la hoja, para que no se corte al desplazarla. */}
                    <div className="sticky left-0" style={containerW ? { width: containerW } : undefined}>
                      {renderExpanded?.(row)}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
          })}
          {data.students.length === 0 && (
            <tr>
              <td colSpan={slots.length + 9} className="px-4 py-10 text-center text-neutral-500">
                Ningún alumno cumple los filtros seleccionados.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
